/// <amd-module name="Build/Admin/Agent/Components/AgentHub" />
import * as React from "react";

import { ObservableValue } from "VSS/Core/Observable";
import { onClickFPS } from "VSS/Platform/FPS";
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";

import { Header, TitleSize } from "VSSUI/Header";
import { HeaderCommandBarWithFilter } from "VSSUI/HeaderCommandBar";
import { Page } from "VSSUI/Page";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { Tab, TabBar } from "VSSUI/Tabs";

import { TaskAgent, TaskAgentPool, TaskAgentPoolOptions } from "DistributedTask/Client/Contracts/TaskAgent";

import { AgentActionCreator } from "Build/Admin/Agent/Actions/AgentActionCreator";
import { AgentCapabilitiesContainer } from "Build/Admin/Agent/Components/AgentCapabilitiesContainer";
import { AgentJobsContainer } from "Build/Admin/Agent/Components/AgentJobsContainer";
import { StoresHub } from "Build/Admin/Agent/Stores/StoresHub";
import { NotificationArea } from "Build/Admin/Common/Components/NotificationArea";
import { AgentHubViewKeys } from "Build/Admin/Common/Constants";
import { IPoolAdminLinkingService } from "Build/Admin/Common/Services/PoolAdminLinkingService";
import { INotification } from "Build/Admin/Common/Stores/NotificationStore";
import { isElastic } from "Build/Admin/Common/TaskAgentPoolUtils";

import * as Resources from "Build/Admin/Agent/Resources";

export interface IAgentHubProps {
    actionCreator: AgentActionCreator;
    storesHub: StoresHub;
}

export interface IAgentHubState {
    selectedAgent: TaskAgent | undefined;
    selectedAgentPool: TaskAgentPool | undefined;
    selectedTabId: string;
    notifications: INotification[];
}

export class AgentHub extends VssComponent<IAgentHubProps, IAgentHubState> {
    private filterToggled: ObservableValue<boolean> = new ObservableValue<boolean>(false);

    constructor(props: IAgentHubProps, context: IVssComponentContext) {
        super(props, context);
        this.state = this._getStateFromStores();
    }

    public render(): JSX.Element {
        // Elastic agent pools do not have support for capabilities
        const renderCapabilitiesTab: boolean = this.state.selectedAgentPool !== undefined && !isElastic(this.state.selectedAgentPool);

        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="pipelines-agent-hub flex-grow">
                    <Header
                        backButtonProps={{
                            onClick: this._onBackButtonClick
                        }}
                        title={this._getPageTitle()}
                        titleSize={TitleSize.Large}
                    />
                    <TabBar
                        renderAdditionalContent={this._renderAdditionalTabBarContent}
                        onSelectedTabChanged={this._onSelectedTabChanged}
                        selectedTabId={this.state.selectedTabId}>
                        <Tab name={Resources.Jobs} id={AgentHubViewKeys.Jobs} />
                        {renderCapabilitiesTab && <Tab name={Resources.Capabilities} id={AgentHubViewKeys.Capabilities} />}
                    </TabBar>
                    <NotificationArea
                        notifications={this.state.notifications}
                        onNotificationDismissed={this._onNotificationDismissed}
                    />
                    {this._getTabContent()}
                </Page>
            </Surface>
        );
    }

    public componentDidMount(): void {
        this.props.storesHub.selectionStore.addChangedListener(this._onStoreChanged);
        this.props.storesHub.notificationStore.addChangedListener(this._onStoreChanged);
    }

    public componentWillUnmount(): void {
        this.props.storesHub.selectionStore.removeChangedListener(this._onStoreChanged);
        this.props.storesHub.notificationStore.removeChangedListener(this._onStoreChanged);
    }

    private _renderAdditionalTabBarContent = (): JSX.Element | undefined => {
        switch (this.state.selectedTabId) {
            case AgentHubViewKeys.Jobs:
                if (this.props.storesHub.jobStore.state.length > 0) {
                    return <HeaderCommandBarWithFilter
                        items={[]}
                        filter={this.props.storesHub.jobStore.filter}
                        filterToggled={this.filterToggled}
                    />;
                }
                else {
                    return undefined;
                }
            default:
                return undefined;
        }
    }

    private _getTabContent = (): JSX.Element => {
        switch (this.state.selectedTabId) {
            case AgentHubViewKeys.Capabilities:
                return <AgentCapabilitiesContainer actionCreator={this.props.actionCreator} storesHub={this.props.storesHub} />;
            case AgentHubViewKeys.Jobs:
                return <AgentJobsContainer
                    actionCreator={this.props.actionCreator}
                    storesHub={this.props.storesHub}
                    filterToggled={this.filterToggled}
                    onFilterToggled={this._onFilterBarToggled}
                />;
            default:
                return <div />;
        }
    }

    private _onBackButtonClick = (): void => {
        const linkingService: IPoolAdminLinkingService = this.context.pageContext.getService<IPoolAdminLinkingService>("IPoolAdminLinkingService");
        if (this.props.storesHub.selectionStore.state.selectedAgentQueue) {
            const agentQueueLink: string = linkingService.getAgentQueueUrl(this.props.storesHub.selectionStore.state.selectedAgentQueue);
            onClickFPS(this.context.pageContext, agentQueueLink, true);
        }
        else if (this.props.storesHub.selectionStore.state.selectedAgentPool) {
            const agentPoolLink: string = linkingService.getAgentPoolUrl(this.props.storesHub.selectionStore.state.selectedAgentPool);
            onClickFPS(this.context.pageContext, agentPoolLink, true);
        }
    }

    private _onFilterBarToggled = (): void => {
        this.filterToggled.value = !this.filterToggled.value;
    }

    private _onStoreChanged = (): void => {
        this.setState(this._getStateFromStores());
    }

    private _getStateFromStores(): IAgentHubState {
        return {
            selectedAgent: this.props.storesHub.selectionStore.state.selectedAgent,
            selectedAgentPool: this.props.storesHub.selectionStore.state.selectedAgentPool,
            selectedTabId: this.props.storesHub.selectionStore.state.selectedTabId,
            notifications: this.props.storesHub.notificationStore.state
        } as IAgentHubState;
    }

    private _onSelectedTabChanged = (newTabId: string) => {
        this.props.actionCreator.updateSelectedTab(newTabId);
    }

    private _onNotificationDismissed = (notification: INotification): void => {
        this.props.actionCreator.notificationActionCreator.dismissNotification(notification);
    }

    private _getPageTitle(): string {
        return this.state.selectedAgent ? this.state.selectedAgent.name : "";
    }
}
